import { ReactNode } from 'react'
import { Controller, useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { Button, Currency, Select } from 'shared/components'
import { usePayoutMethodLabels, useUserRoles } from 'shared/hooks'
import { WithClassName } from 'shared/types/general.types'
import { cn } from 'utils/style'
import { useStatusOptions } from '../../hooks/useStatusOptions'
import { useEditPayoutMutation } from '../../mutations/useEditPayoutMutation'
import { formatPayoutDate } from '../../utils/payout'
import type { PayoutItem, PayoutStatus } from '../../types/payouts.types'

type PayoutFormProps = WithClassName<{
	payout: PayoutItem
	onSuccess: () => void
}>

type PayoutFormValues = {
	tx_reference: string
	status: { value: PayoutStatus; label: string } | null
}

type FieldProps = WithClassName<{
	label: string
	htmlFor?: string
	children: ReactNode
}>

const labelClassName =
	'mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'
const boxClassName = 'h-10 w-full rounded-md border border-border px-3 text-[13px]'
const sectionClassName = 'rounded-lg border border-border p-4'
const sectionTitleClassName = 'mb-4 font-display text-[14px] font-semibold text-foreground'

const Field = ({ label, htmlFor, className, children }: FieldProps) => (
	<div className={cn('min-w-0', className)}>
		{htmlFor ? (
			<label htmlFor={htmlFor} className={labelClassName}>
				{label}
			</label>
		) : (
			<span className={labelClassName}>{label}</span>
		)}
		{children}
	</div>
)

const ReadOnlyField = ({ label, className, children }: FieldProps) => (
	<Field label={label} className={className}>
		<div
			className={cn(
				boxClassName,
				'truncate bg-muted/50 tabular-nums leading-[38px] text-muted-foreground'
			)}>
			{children || '—'}
		</div>
	</Field>
)

export const PayoutForm = ({ payout, className, onSuccess }: PayoutFormProps) => {
	const { t } = useTranslation()
	const { isUser } = useUserRoles()
	const payoutMethodLabels = usePayoutMethodLabels()
	const statusOptions = useStatusOptions()
	const { editPayoutMutation, isEditPayoutLoading } = useEditPayoutMutation({ onSuccess })
	const { register, control, handleSubmit } = useForm<PayoutFormValues>({
		defaultValues: {
			tx_reference: payout.tx_reference ?? '',
			status: statusOptions.find((option) => option.value === payout.status) ?? null
		}
	})

	const onSubmit = ({ tx_reference, status }: PayoutFormValues) => {
		editPayoutMutation({ id: payout.id, data: { status: status?.value, tx_reference } })
	}

	return (
		<form className={cn('space-y-4', className)} onSubmit={handleSubmit(onSubmit)}>
			<section className={sectionClassName}>
				<h3 className={sectionTitleClassName}>{t('User information')}</h3>
				<div className="grid gap-4 sm:grid-cols-2">
					<ReadOnlyField label={t('Payout')}>{payout.reference}</ReadOnlyField>
					<ReadOnlyField label={t('Created')}>{formatPayoutDate(payout.created)}</ReadOnlyField>
					<ReadOnlyField label={t('Name')}>{payout.account?.name}</ReadOnlyField>
					<ReadOnlyField label={t('Email')}>{payout.account?.email}</ReadOnlyField>
					<ReadOnlyField className="sm:col-span-2" label={t('SSN')}>
						{payout.account?.ssn}
					</ReadOnlyField>
				</div>
			</section>

			<section className={sectionClassName}>
				<h3 className={sectionTitleClassName}>{t('Payout & amount')}</h3>
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
					<ReadOnlyField label={t('Payout method')}>
						{payout.payout_method
							? payoutMethodLabels[payout.payout_method.kind]
							: t('No payout method')}
					</ReadOnlyField>
					<ReadOnlyField className="sm:col-span-2" label={t('Payout details')}>
						{payout.payout_method?.details}
					</ReadOnlyField>
					<ReadOnlyField label={t('Currency')}>{payout.amount?.currency}</ReadOnlyField>
					<ReadOnlyField label={t('Cost')}>
						<Currency price={payout.takedown_and_storage_fees} />
					</ReadOnlyField>
					<ReadOnlyField label={t('Amount')}>
						<Currency price={payout.amount} />
					</ReadOnlyField>
				</div>
				<Field className="mt-4" label={t('Transaction Reference')} htmlFor="tx_reference">
					<input
						id="tx_reference"
						placeholder={t('e.g. bank transfer reference')}
						readOnly={isUser}
						className={cn(
							boxClassName,
							'bg-background text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20',
							isUser && 'bg-muted/50 text-muted-foreground'
						)}
						{...register('tx_reference')}
					/>
				</Field>
			</section>

			<section className={sectionClassName}>
				<h3 className={sectionTitleClassName}>{t('Payment')}</h3>
				<Field label={t('Status')} htmlFor="payout-status">
					<Controller
						name="status"
						control={control}
						render={({ field }) => (
							<Select
								id="payout-status-select"
								inputId="payout-status"
								variant="form"
								isClearable={false}
								placeholder={t('Default')}
								options={statusOptions}
								menuPlacement="auto"
								menuPosition="fixed"
								{...field}
								isDisabled={isUser}
							/>
						)}
					/>
				</Field>
			</section>

			{!isUser && (
				<Button
					type="submit"
					isLoading={isEditPayoutLoading}
					className="h-11 w-full rounded-md px-4 text-sm font-medium shadow">
					{t('Update')}
				</Button>
			)}
		</form>
	)
}
